import { NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, NavigationEnd, RouterLink } from '@angular/router';

import { PrecinctType } from '@type/precinct.type';
import { ProjectType } from '@type/project.type';
import { filter } from 'rxjs';

import { AppIconComponent } from '@components/@fec/app-icon.component';
import { SwitchInputComponent } from '@components/@fec/switch-input.component';
import { TextInputComponent } from '@components/@fec/text-input.component';

import { PrecinctService } from '@services/api/precinct.service';
import { ProjectService } from '@services/api/project.service';
import { ApiService } from '@services/helper/api-wrapper.service';

@Component({
  selector: 'app-project-navigator-dropdown',
  imports: [
    TextInputComponent,
    SwitchInputComponent,
    RouterLink,
    NgOptimizedImage,
    NgTemplateOutlet,
    FormsModule,
    AppIconComponent,
  ],
  templateUrl: './project-navigator-dropdown.component.html',
  styleUrl: './project-navigator-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectNavigatorDropdownComponent {
  public itemSelected = output<void>();

  // ─── Only fetch when the dropdown is actually open ───────────────
  public enabled = input<boolean>(false);

  /**
   * Rendering style (FEC-1150).
   *
   * `dropdown` — the desktop popover: floating card with its own shadow.
   * `inline`   — the responsive menu: sits inside the mobile menu card, so it
   *              drops the card chrome and lets each precinct collapse.
   */
  public variant = input<'dropdown' | 'inline'>('dropdown');

  /**
   * Whether the "add a project" shortcut sits beside the search field
   * (FEC-1150 QA — the header's global + is gone on mobile, so the action lives
   * in the open menu instead). The caller owns the permission check.
   */
  public showAddProject = input<boolean>(false);

  protected precinctService = inject(PrecinctService);
  protected projectService = inject(ProjectService);
  protected apiService = inject(ApiService);
  private router = inject(Router);

  public searchTerm = signal<string>('');

  /**
   * Whether every project — archived, draft and offline included — is listed
   * alongside the live ones. Off by default: browsing shows LIVE projects only.
   * Note this gates the *browse* list only: searching always spans everything
   * (a name you type is a name you expect to find).
   */
  public showAll = signal<boolean>(false);

  /**
   * Label for the toggle. It describes the action the toggle performs next, not
   * the current state — without that the switch gives no clue what it just did.
   */
  public readonly toggleLabel = computed(() =>
    this.showAll() ? 'Show Live Projects' : 'Show All Projects',
  );

  /**
   * Id shared by the "Show All" switch and its label. The desktop popover and
   * the responsive menu can both be in the DOM, so the id is scoped per variant
   * to keep it unique (duplicate ids break the label ↔ control association).
   */
  public readonly toggleId = computed(() =>
    this.variant() === 'inline'
      ? 'project-navigator-show-all-inline'
      : 'project-navigator-show-all',
  );

  /** Outer container — a floating card on desktop, plain content when inline. */
  protected readonly wrapperClass = computed(() =>
    this.variant() === 'inline'
      ? 'flex flex-col justify-start items-stretch gap-2 text-dark dark:text-white'
      : 'w-81 flex flex-col justify-start items-stretch gap-1 p-4 rounded-2xl shadow-[0_0_35.2px_0_rgba(0,0,0,0.07)] bg-white dark:bg-surface-dark text-dark dark:text-white -my-1',
  );

  /** Search field — 40px tall / 12px radius in the responsive menu (FEC-1150). */
  protected readonly searchInputClass = computed(() => {
    const shared =
      '!bg-muted dark:bg-surface-dark-muted dark:!bg-surface-dark-muted placeholder:!text-dark-grey dark:text-grey dark:placeholder:!text-grey border-transparent focus:!border-transparent';
    return this.variant() === 'inline'
      ? `!h-10 !py-2.5 !px-4 !pl-11 !rounded-xl !text-sm ${shared}`
      : `!h-11 !py-3 !px-4 !pl-12 ${shared}`;
  });

  /** Magnifier overlaid on the search field, sized to match the field. */
  protected readonly searchIconClass = computed(() =>
    this.variant() === 'inline'
      ? 'absolute left-3.5 top-1/2 w-5 h-4 -translate-y-1/2 dark:invert'
      : 'absolute left-4 top-1/2 w-[24px] h-[18px] -translate-y-1/2 dark:invert',
  );

  /**
   * The precinct group the user picked in the `inline` variant.
   *
   * Only one group is open at a time (FEC-1150 QA) so the responsive menu stays
   * short enough to scroll as a single list. `undefined` means "untouched" — the
   * first group then opens by default; `null` means the user closed them all.
   */
  private readonly selectedPrecinct = signal<string | null | undefined>(undefined);

  /** The single expanded group — the user's pick, else the first one listed. */
  private readonly openPrecinct = computed<string | null>(() => {
    const selected = this.selectedPrecinct();
    if (selected !== undefined) return selected;
    const [first] = this.filteredPrecinctListing();
    return first ? (first.name ?? '') : null;
  });

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.searchTerm.set('');
        this.showAll.set(false);
        this.selectedPrecinct.set(undefined);
      });

    // Watch the enabled input — when dropdown opens, fetch both queries
    effect(() => {
      if (this.enabled()) {
        this.queryGetShortPrecinctList.refetch();
        this.queryGetAllSoloProjects.refetch();
      }
    });
  }

  private queryGetShortPrecinctList = this.precinctService.queryGetShortPrecinctList({
    refetchOnWindowFocus: false,
    enabled: false,
    staleTime: 3 * 60 * 1000, // 3 minutes
    select: (response) => {
      const { status, data } = response as any;
      if (status) {
        return (
          (data?.data || [])?.filter((precinct: PrecinctType) => precinct.projects.length > 0) || []
        );
      } else {
        return [];
      }
    },
  });

  private queryGetAllSoloProjects = this.projectService.queryGetAllSoloProjects({
    refetchOnWindowFocus: false,
    enabled: false,
    staleTime: 3 * 60 * 1000, // 3 minutes
    select: (response) => {
      const { status, data } = response as any;
      if (status) {
        return data?.data || [];
      } else {
        return [];
      }
    },
  });

  /**
   * Whether a project is LIVE — the only state the browse list shows by default.
   *
   * Mirrors the dashboard's status split (see `PrecinctProjectService`):
   *   live      → published && !archive && !never_published
   *   offline   → !published (a.k.a. "Coming Soon")
   *   draft     → never_published
   *   archived  → archive
   *
   * The API is inconsistent about these flags — they come back as `true`, `1`,
   * `0`, `false`, or absent depending on the endpoint — so compare on
   * truthiness rather than identity.
   */
  private isLive(project: ProjectType): boolean {
    return !!project?.published && !project?.archive && !project?.never_published;
  }

  public filteredPrecinctListing = computed(() => {
    const search = this.searchTerm().toLowerCase().trim();
    const showAll = this.showAll();
    const precincts: PrecinctType[] = this.queryGetShortPrecinctList.data() || [];
    const soloProjects: ProjectType[] = this.queryGetAllSoloProjects.data() || [];

    const allPrecincts = [...precincts];
    if (soloProjects.length > 0) {
      allPrecincts.push({
        name: 'Solo Projects',
        projects: soloProjects,
        precinct_id: null,
      } as any);
    }

    /* Searching deliberately spans every project state regardless of the
     * toggle — a name you type is a name you expect to find. The toggle only
     * governs what the untouched browse list shows. */
    if (!search && showAll) {
      return allPrecincts;
    }

    return allPrecincts
      .map((precinct) => {
        const filteredProjects = (precinct.projects || []).filter((project) => {
          if (search) {
            return (project.name ?? '')?.toLowerCase().includes(search);
          }
          return this.isLive(project);
        });
        return { ...precinct, projects: filteredProjects };
      })
      .filter((precinct) => precinct.projects.length > 0);
  });

  /**
   * Whether a precinct group is expanded (its projects listed).
   * A search always wins — matches stay visible regardless of accordion state.
   */
  public isPrecinctExpanded(name: string | undefined): boolean {
    if (this.searchTerm().trim()) return true;
    return this.openPrecinct() === (name ?? '');
  }

  /**
   * Opens a precinct group in the `inline` variant, closing whichever was open
   * — re-tapping the open group closes it and leaves the list fully collapsed.
   */
  public togglePrecinct(name: string | undefined): void {
    const key = name ?? '';
    this.selectedPrecinct.set(this.openPrecinct() === key ? null : key);
  }
}
